"use strict";
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _Channel_connectionManager, _Channel_internalEmitter, _Channel_readyToConnect;
Object.defineProperty(exports, "__esModule", { value: true });
exports.Channel = void 0;
/* eslint-disable no-console */
const events_1 = require("events");
const lazy_1 = require("../../../util/lazy");
const base_1 = require("../../base");
const client_1 = require("./client");
const connection_manager_1 = require("./connection-manager");
const provider_1 = require("./provider");
function retryDelay(count) {
    const interval = 500; // base delay
    const steps = 10; // How many retries to do before incrementing the delay
    const base = 2; // How much to multiply the previous delay by
    const max = 30000; // max delay
    const step = Math.floor(count / steps);
    const delay = Math.min(max, interval * base ** step);
    return new Promise(resolve => {
        setTimeout(() => {
            resolve(false);
        }, delay);
    });
}
class Channel extends base_1.EmitterBase {
    constructor(wire) {
        super(wire, 'channel');
        _Channel_connectionManager.set(this, void 0);
        _Channel_internalEmitter.set(this, new events_1.EventEmitter());
        // OpenFin API has not been injected at construction time, *must* wait for API to be ready.
        _Channel_readyToConnect.set(this, new lazy_1.AsyncRetryableLazy(async () => {
            await Promise.all([
                this.on('disconnected', (eventPayload) => {
                    client_1.default.handleProviderDisconnect(eventPayload);
                }),
                this.on('connected', (...args) => {
                    __classPrivateFieldGet(this, _Channel_internalEmitter, "f").emit('connected', ...args);
                })
            ]).catch(() => new Error('error setting up channel connection listeners'));
        }));
        __classPrivateFieldSet(this, _Channel_connectionManager, new connection_manager_1.ConnectionManager(wire), "f");
    }
    async getAllChannels() {
        return this.wire.sendAction('get-all-channels').then(({ payload }) => payload.data);
    }
    async onChannelConnect(listener) {
        await this.on('connected', listener);
    }
    async onChannelDisconnect(listener) {
        await this.on('disconnected', listener);
    }
    async safeConnect(channelName, shouldWait, connectPayload) {
        const retryInfo = { count: 0 };
        /* eslint-disable no-await-in-loop, no-constant-condition */
        do {
            // setup a listener and a connected promise to await in case we connect before the channel is ready
            let connectedListener = () => undefined;
            const connectedPromise = new Promise((resolve) => {
                connectedListener = (payload) => {
                    if (channelName === payload.channelName) {
                        resolve(true);
                    }
                };
                __classPrivateFieldGet(this, _Channel_internalEmitter, "f").on('connected', connectedListener);
            });
            try {
                if (retryInfo.count > 0) {
                    // Wait before retrying
                    // Delay returns false connectedPromise returns true so we can know if a retry is due to connected event
                    retryInfo.gotConnectedEvent = await Promise.race([retryDelay(retryInfo.count), connectedPromise]);
                    const result = await this.wire.sendAction('connect-to-channel', { ...connectPayload, retryInfo });
                    // log only if there was a retry
                    console.log(`Successfully connected to channelName: ${channelName}`);
                    return result.payload.data;
                }
                // Send retryInfo to the core for debug log inclusion
                const sentMessagePromise = this.wire.sendAction('connect-to-channel', connectPayload);
                // Save messageId from the first connection attempt
                retryInfo.originalMessageId = sentMessagePromise.messageId;
                const result = await sentMessagePromise;
                return result.payload.data;
            }
            catch (error) {
                if (!error.message.includes('internal-nack')) {
                    // Not an internal nack, break the loop
                    throw error;
                }
                if (shouldWait && retryInfo.count === 0) {
                    // start waiting on the next iteration, warn the user
                    console.warn(`No channel found for channelName: ${channelName}. Waiting for connection...`);
                }
            }
            finally {
                retryInfo.count += 1;
                // in case of other errors, remove our listener
                __classPrivateFieldGet(this, _Channel_internalEmitter, "f").removeListener('connected', connectedListener);
            }
        } while (shouldWait); // If we're waiting we retry the above loop
        // Should wait was false, no channel was found.
        throw new Error(`No channel found for channelName: ${channelName}.`);
        /* eslint-enable no-await-in-loop, no-constant-condition */
    }
    async connect(channelName, options = {}) {
        // Make sure we don't connect before listeners are set up
        // This also errors if we're not in OpenFin, ensuring we don't run unnecessary code
        await __classPrivateFieldGet(this, _Channel_readyToConnect, "f").getValue();
        if (!channelName || typeof channelName !== 'string') {
            throw new Error('Please provide a channelName string to connect to a channel.');
        }
        const opts = { wait: true, ...this.wire.environment.getDefaultChannelOptions().connect, ...options };
        const { offer, rtc: rtcPacket } = await __classPrivateFieldGet(this, _Channel_connectionManager, "f").createClientOffer(opts);
        let connectionUrl;
        if (this.fin.me.isFrame || this.fin.me.isView || this.fin.me.isWindow) {
            connectionUrl = (await this.fin.me.getInfo()).url;
        }
        const connectPayload = {
            channelName,
            ...opts,
            offer,
            connectionUrl
        };
        const routingInfo = await this.safeConnect(channelName, opts.wait, connectPayload);
        const strategy = await __classPrivateFieldGet(this, _Channel_connectionManager, "f").createClientStrategy(rtcPacket, routingInfo);
        const channel = new client_1.default(routingInfo, this.wire, strategy);
        // It is the client's responsibility to handle endpoint disconnection to the provider.
        // If the endpoint dies, the client will force a disconnection through the core.
        // The provider does not care about endpoint disconnection.
        strategy.onEndpointDisconnect(routingInfo.channelId, async () => {
            try {
                await channel.sendDisconnectAction();
            }
            catch (error) {
                console.warn(`Something went wrong during disconnect for client with uuid: ${routingInfo.uuid} / name: ${routingInfo.name} / endpointId: ${routingInfo.endpointId}.`);
            }
            finally {
                client_1.default.handleProviderDisconnect(routingInfo);
            }
        });
        return channel;
    }
    async create(channelName, options) {
        if (!channelName) {
            throw new Error('Please provide a channelName to create a channel');
        }
        const { payload: { data: providerIdentity } } = await this.wire.sendAction('create-channel', { channelName });
        const channel = __classPrivateFieldGet(this, _Channel_connectionManager, "f").createProvider(options, providerIdentity);
        // TODO: fix typing (internal)
        // @ts-expect-error
        this.on('client-disconnected', (eventPayload) => {
            if (eventPayload.channelName === channelName) {
                provider_1.ChannelProvider.handleClientDisconnection(channel, eventPayload);
            }
        });
        return channel;
    }
}
exports.Channel = Channel;
_Channel_connectionManager = new WeakMap(), _Channel_internalEmitter = new WeakMap(), _Channel_readyToConnect = new WeakMap();
